import { ActionMenu } from '@/common/components/ActionMenu';
import UserAvatar from '@/common/components/UserAvatars/UserAvatar';
import { BaseCommentWithMentionsAndUser } from '@/common/types/databaseTypes';
import { ActionIcon, Group, Text, Tooltip } from '@mantine/core';
import { IconEdit, IconMessageCircle, IconTrash } from '@tabler/icons-react';
import { formatDistanceToNow } from 'date-fns';
import { getDateFnsLocale } from '@/common/utils/i18nUtils';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useAuthStore } from '@/stores/authStore';

interface CommentHeaderProps {
    comment: BaseCommentWithMentionsAndUser;
    isReply: boolean;
    onReplyClick?: () => void;
    onEditClick?: () => void;
    onDeleteClick?: () => void;
    showReplyButton: boolean;
}

export const CommentHeader: React.FC<CommentHeaderProps> = ({
    comment,
    isReply,
    onReplyClick,
    onEditClick,
    onDeleteClick,
    showReplyButton,
}) => {
    const { user } = useAuthStore();
    const { t } = useTranslation(['common', 'comment']);

    return (
        <Group justify="space-between">
            <Group gap={4}>
                <UserAvatar
                    name={comment.user?.name}
                    size={16}
                    imageUrl={comment.user?.profile_image_url}
                />
                <Text size="sm" span>
                    <Trans
                        i18nKey="activity:actor"
                        components={[
                            <Text
                                component="span"
                                size="sm"
                                fw={600}
                                key="bold"
                            />,
                        ]}
                        values={{ name: comment.user?.name }}
                    />
                    <Text span>
                        {isReply
                            ? t('comment:reply.header_text')
                            : t('comment:comment.header_text')}
                    </Text>
                </Text>
                <Text size="xs" c="dimmed" pl={2}>
                    {formatDistanceToNow(new Date(comment.register_date), {
                        addSuffix: true,
                        locale: getDateFnsLocale(),
                    })}
                    {comment.update_date &&
                        comment.register_date !== comment.update_date && (
                            <Text span ml={4} c="dimmed" fz={10}>
                                ({t('action.edited')})
                            </Text>
                        )}
                </Text>
            </Group>

            <Group gap={8}>
                {showReplyButton && onReplyClick && (
                    <Tooltip label={t('comment:reply.add')} withArrow>
                        <ActionIcon
                            variant="transparent"
                            size="xs"
                            onClick={onReplyClick}
                        >
                            <IconMessageCircle size={14} color="gray" />
                        </ActionIcon>
                    </Tooltip>
                )}

                {user?.id === comment.register_id && (
                    <ActionMenu
                        items={[
                            {
                                id: 'edit',
                                label: t('action.edit'),
                                icon: <IconEdit size={14} />,
                                onClick: onEditClick,
                            },
                            {
                                id: 'delete',
                                label: t('action.delete'),
                                icon: <IconTrash size={14} />,
                                color: 'red',
                                onClick: onDeleteClick,
                            },
                        ]}
                    />
                )}
            </Group>
        </Group>
    );
};
